
// Angell: screen-space ambient occlusion for point clouds.
// Input: a float colour target whose alpha holds log2(linear view depth) (0 or 1 = background), as produced by
// the EDL colour pass and the HQ-splat depth pass. Pipeline, all at half resolution:
//   1. ao_depth.fs   downsample with a 3x3 nearest-depth fill -> linear depth buffer
//   2. ao.fs         normal-oriented hemisphere SSAO (16 samples, 4x4 rotation noise, world-metre radius)
//   3. ao_blur.fs    separable depth-aware blur, horizontal then vertical
// The composite (ao_composite.glsl) upsamples depth-aware inside the EDL / normalisation shaders.

import * as THREE from "../../libs/three.js/build/three.module.js";
import {Shaders} from "../../build/shaders/shaders.js";
import {Utils} from "../utils.js";

const KERNEL_SIZE = 16;

function mulberry32(seed){
	return function(){
		seed |= 0; seed = seed + 0x6D2B79F5 | 0;
		let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
		t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
		return ((t ^ t >>> 14) >>> 0) / 4294967296;
	};
}

function makeKernel(){
	const rand = mulberry32(1337);
	const kernel = new Float32Array(KERNEL_SIZE * 3);
	for(let i = 0; i < KERNEL_SIZE; i++){
		let v = new THREE.Vector3(rand() * 2 - 1, rand() * 2 - 1, rand() * 0.85 + 0.15);
		v.normalize();
		v.multiplyScalar(rand());
		const t = i / KERNEL_SIZE;
		const scale = 0.1 + 0.9 * t * t;
		v.multiplyScalar(scale);
		kernel[i * 3 + 0] = v.x;
		kernel[i * 3 + 1] = v.y;
		kernel[i * 3 + 2] = v.z;
	}
	return kernel;
}

function makeNoise(){
	const rand = mulberry32(4242);
	const data = new Uint8Array(4 * 4 * 4);
	for(let i = 0; i < 16; i++){
		data[i * 4 + 0] = Math.floor(rand() * 255);
		data[i * 4 + 1] = Math.floor(rand() * 255);
		data[i * 4 + 2] = 128;
		data[i * 4 + 3] = 255;
	}
	const tex = new THREE.DataTexture(data, 4, 4, THREE.RGBAFormat);
	tex.wrapS = THREE.RepeatWrapping;
	tex.wrapT = THREE.RepeatWrapping;
	tex.minFilter = THREE.NearestFilter;
	tex.magFilter = THREE.NearestFilter;
	tex.needsUpdate = true;
	return tex;
}

function makeTarget(){
	return new THREE.WebGLRenderTarget(1, 1, {
		minFilter: THREE.NearestFilter,
		magFilter: THREE.NearestFilter,
		format: THREE.RGBAFormat,
		type: THREE.FloatType,
		depthBuffer: false,
		stencilBuffer: false,
	});
}

function makeMaterial(fragmentName, uniforms, defines = ""){
	const m = new THREE.RawShaderMaterial({
		uniforms: uniforms,
		vertexShader: Shaders['edl.vs'],
		fragmentShader: defines + Shaders[fragmentName],
	});
	m.depthTest = false;
	m.depthWrite = false;
	m.transparent = false;
	return m;
}

export class ScreenSpaceAO{

	constructor(renderer){
		this.renderer = renderer;
		this.width = 0;
		this.height = 0;

		this.rtDepth = makeTarget();
		this.rtAO = makeTarget();
		this.rtBlur = makeTarget();

		this.noise = makeNoise();
		this.kernel = makeKernel();

		this.depthMaterial = makeMaterial('ao_depth.fs', {
			uSource: {type: 't', value: null},
			uSourceSize: {type: '2fv', value: [1, 1]},
		});

		this.aoMaterial = makeMaterial('ao.fs', {
			uDepth: {type: 't', value: null},
			uNoise: {type: 't', value: this.noise},
			uSize: {type: '2fv', value: [1, 1]},
			uProj: {type: 'm4', value: new THREE.Matrix4()},
			uProjInv: {type: 'm4', value: new THREE.Matrix4()},
			uOrtho: {type: 'b', value: false},
			uRadius: {type: 'f', value: 1.0},
			uKernel: {type: '3fv', value: this.kernel},
		}, `#define KERNEL_SIZE ${KERNEL_SIZE}\n`);

		this.blurMaterial = makeMaterial('ao_blur.fs', {
			uAO: {type: 't', value: null},
			uDepth: {type: 't', value: null},
			uSize: {type: '2fv', value: [1, 1]},
			uDir: {type: '2fv', value: [1, 0]},
			uRadius: {type: 'f', value: 1.0},
		});
	}

	resize(width, height){
		const w = Math.max(1, Math.round(width / 2));
		const h = Math.max(1, Math.round(height / 2));
		if(w !== this.width || h !== this.height){
			this.width = w;
			this.height = h;
			this.rtDepth.setSize(w, h);
			this.rtAO.setSize(w, h);
			this.rtBlur.setSize(w, h);
		}
	}

	// source: texture with log2 linear depth in alpha; returns {ao, depth, size}
	render(source, camera, width, height, params){
		const renderer = this.renderer;
		this.resize(width, height);
		const size = [this.width, this.height];
		const radius = Math.max(0.05, params.radius || 1.0);

		const oldTarget = renderer.getRenderTarget();

		// The Potree point renderer drives GL directly and can leave additive blending enabled (HQ attribute pass)
		// while three.js believes blending is off after resetState(); force the fixed-function state we need.
		const gl = renderer.getContext();
		gl.disable(gl.BLEND);
		gl.disable(gl.DEPTH_TEST);
		gl.depthMask(false);

		{ // 1. downsample + fill
			const u = this.depthMaterial.uniforms;
			u.uSource.value = source;
			u.uSourceSize.value = [width, height];
			renderer.setRenderTarget(this.rtDepth);
			Utils.screenPass.render(renderer, this.depthMaterial);
		}

		{ // 2. occlusion
			const u = this.aoMaterial.uniforms;
			u.uDepth.value = this.rtDepth.texture;
			u.uSize.value = size;
			u.uProj.value.copy(camera.projectionMatrix);
			u.uProjInv.value.copy(camera.projectionMatrix).invert();
			u.uOrtho.value = camera.isOrthographicCamera === true;
			u.uRadius.value = radius;
			renderer.setRenderTarget(this.rtAO);
			Utils.screenPass.render(renderer, this.aoMaterial);
		}

		{ // 3. bilateral blur, two passes
			const u = this.blurMaterial.uniforms;
			u.uDepth.value = this.rtDepth.texture;
			u.uSize.value = size;
			u.uRadius.value = radius;

			u.uAO.value = this.rtAO.texture;
			u.uDir.value = [1, 0];
			renderer.setRenderTarget(this.rtBlur);
			Utils.screenPass.render(renderer, this.blurMaterial);

			u.uAO.value = this.rtBlur.texture;
			u.uDir.value = [0, 1];
			renderer.setRenderTarget(this.rtAO);
			Utils.screenPass.render(renderer, this.blurMaterial);
		}

		renderer.setRenderTarget(oldTarget);

		return {ao: this.rtAO.texture, depth: this.rtDepth.texture, size: size};
	}
}
